/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import shortid from 'shortid';
import classNames from 'classnames';

import ExpandableText from 'components/ExpandableText/ExpandableText';

import styles from './Accordion.module.scss';

/**
 * This is the Accordion component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Accordion({ items, multiple, nested }) {
  const [expanded, setExpanded] = useState([]);
  const [id] = useState(shortid.generate());

  const toggleExpanded = (index) => {
    if (multiple) {
      if (expanded.includes(index)) {
        setExpanded(expanded.filter((i) => i !== index));
      } else {
        setExpanded([...expanded, index]);
      }
    } else {
      if (expanded.includes(index)) {
        setExpanded([]);
      } else {
        setExpanded([index]);
      }
    }
  };

  return (
    <div
      className={classNames(styles.main, {
        [styles.nested]: nested,
      })}
    >
      <div className={styles.items}>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {item.items && item.items.length == 0 ? (
              <h3>{item.heading}</h3>
            ) : (
              <>
                <h3>
                  <button
                    type="button"
                    onClick={() => toggleExpanded(index)}
                    aria-expanded={expanded.includes(index)}
                    aria-controls={`${id}_${index}`}
                    id={`${id}_${index}_button`}
                  >
                    {item.heading}
                    <span className="plus-icon"></span>
                  </button>
                </h3>
                <div
                  role="region"
                  id={`${id}_${index}`}
                  aria-labelledby={`${id}_${index}_button`}
                >
                  <ExpandableText
                    expanded={expanded.includes(index)}
                    body={item.body}
                    items={item.items}
                    multiple={multiple}
                  ></ExpandableText>
                </div>
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Accordion.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape),
  multiple: PropTypes.bool,
  nested: PropTypes.bool,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Accordion.defaultProps = {
  items: [],
  multiple: true,
  nested: false,
};

export default Accordion;
